import React, { useEffect, useState } from "react";
import { DashboardService } from "../../services/analytics/dashboardService";
import { AnalyticsService } from "../../services/analytics/analyticsService";
import { DashboardCharts } from "../../components/analytics/DashboardCharts";
import { Building, CreditCard, TrendingUp, AlertTriangle, Download } from "lucide-react";
import { toast } from "react-hot-toast";
import { exportToCSV } from "../../utils/exportUtils";
import { useTranslation } from "react-i18next";

interface StatusDistribution {
  payees_pct: number;
  en_attente_pct: number;
  rejetees_pct: number;
}

interface Trends {
  volume_facture_change: number;
  volume_paye_change: number;
}

export const AdminAnalytics: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState<any>(null);
  const [statusDist, setStatusDist] = useState<StatusDistribution>({ payees_pct: 0, en_attente_pct: 0, rejetees_pct: 0 });
  const [trends, setTrends] = useState<Trends>({ volume_facture_change: 0, volume_paye_change: 0 });
  const [alertsCount, setAlertsCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await DashboardService.getAdminStats();
        setStats(data);
      } catch (error) {
        console.error("Error fetching admin stats", error);
        toast.error(t('common.errorGroup'));
      } finally {
        setLoading(false);
      }
    };

    const fetchGlobalStats = async () => {
      try {
        const res = await AnalyticsService.getGlobalStats();
        if (res.data?.status_distribution) setStatusDist(res.data.status_distribution);
        if (res.data?.trends) setTrends(res.data.trends);
      } catch (error) {
        console.error("Error fetching global stats", error);
      }
    };

    const fetchAlerts = async () => {
      try {
        const res = await AnalyticsService.getAlerts();
        setAlertsCount(res.data?.count ?? 0);
      } catch (error) {
        console.error("Error fetching alerts count", error);
      }
    };

    fetchStats();
    fetchGlobalStats();
    fetchAlerts();
  }, []);

  const metrics = [
    {
      title: t('analytics.metrics.invoicedVolume'),
      value: stats?.factures?.montant_total 
        ? new Intl.NumberFormat(i18n.language.startsWith('fr') ? 'fr-FR' : 'en-US', { style: "currency", currency: "XOF" }).format(stats.factures.montant_total)
        : "0 FCFA",
      change: trends.volume_facture_change >= 0 ? `+${trends.volume_facture_change}%` : `${trends.volume_facture_change}%`,
      trend: trends.volume_facture_change >= 0 ? "up" : "down",
      icon: TrendingUp,
      color: "blue",
    },
    {
      title: t('analytics.metrics.paidVolume'),
      value: stats?.paiements?.montant_total
        ? new Intl.NumberFormat(i18n.language.startsWith('fr') ? 'fr-FR' : 'en-US', { style: "currency", currency: "XOF" }).format(stats.paiements.montant_total)
        : "0 FCFA",
      change: trends.volume_paye_change >= 0 ? `+${trends.volume_paye_change}%` : `${trends.volume_paye_change}%`,
      trend: trends.volume_paye_change >= 0 ? "up" : "down",
      icon: CreditCard,
      color: "green",
    },
    {
      title: t('analytics.metrics.activeClients'),
      value: stats?.clients?.actifs?.toString() || "0",
      change: `${stats?.clients?.total || 0} ${t('dashboard.stats.total')}`,
      trend: "neutral",
      icon: Building,
      color: "purple",
    },
    {
      title: t('analytics.metrics.riskAlerts'),
      value: alertsCount.toString(),
      change: "",
      trend: "neutral",
      icon: AlertTriangle,
      color: "orange",
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('analytics.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {t('analytics.overview')}
          </p>
        </div>
        <button 
          onClick={() => exportToCSV(metrics, "admin_analytics_rapport")}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none">
            <Download className="h-4 w-4 mr-2" />
            {t('analytics.exportReport')}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {metrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div key={index} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`p-3 rounded-lg bg-${metric.color}-100`}>
                      <Icon className={`h-6 w-6 text-${metric.color}-600`} />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {metric.title}
                      </dt>
                      <dd>
                        <div className="text-2xl font-semibold text-gray-900">
                          {metric.value}
                        </div>
                        <div className="flex items-center text-sm">
                            <span className={`font-medium ${
                                metric.trend === 'up' ? 'text-success-600' : 
                                metric.trend === 'down' ? 'text-error-600' : 'text-gray-500'
                            }`}>
                                {metric.change}
                            </span>
                            {metric.trend !== 'neutral' && <span className="ml-1 text-gray-400">{t('analytics.metrics.vsLastMonth')}</span>}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                {t('analytics.charts.transactionalVolume')}
            </h3>
            <DashboardCharts />
          </div>
          
          <div className="bg-white shadow rounded-lg p-6">
             <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                {t('analytics.charts.statusDistribution')}
            </h3>
            <div className="space-y-4">
                <div>
                    <div className="flex justify-between text-sm font-medium text-gray-900">
                        <span>{t('analytics.charts.paidInvoices')}</span>
                        <span>{statusDist.payees_pct}%</span>
                    </div>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-success-600 h-2.5 rounded-full" style={{ width: `${statusDist.payees_pct}%` }}></div>
                    </div>
                </div>
                <div>
                    <div className="flex justify-between text-sm font-medium text-gray-900">
                        <span>{t('analytics.charts.pendingInvoices')}</span>
                        <span>{statusDist.en_attente_pct}%</span>
                    </div>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-warning-500 h-2.5 rounded-full" style={{ width: `${statusDist.en_attente_pct}%` }}></div>
                    </div>
                </div>
                <div>
                    <div className="flex justify-between text-sm font-medium text-gray-900">
                        <span>{t('analytics.charts.rejectedInvoices')}</span>
                        <span>{statusDist.rejetees_pct}%</span>
                    </div>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-error-500 h-2.5 rounded-full" style={{ width: `${statusDist.rejetees_pct}%` }}></div>
                    </div>
                </div>
            </div>
          </div>
      </div>
    </div>
  );
};
